import React, { useMemo, useState } from 'react';
import Modal from '../../Modal';
import {
    DELIMITER_OPTIONS,
    autoMapFields,
    detectDelimiter,
    parseDelimitedText,
} from './delimitedFile';
import './BulkImporter.css';

const DEFAULT_ACCEPT = [
    '.csv',
    '.tsv',
    '.tab',
    '.txt',
    '.psv',
    'text/csv',
    'text/tab-separated-values',
    'text/plain',
].join(',');

function readFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('The selected file could not be read.'));
        reader.readAsText(file);
    });
}

function normalizeErrors(errors) {
    if (!errors) return [];
    return (Array.isArray(errors) ? errors : [errors]).filter(Boolean).map(String);
}

function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${Math.round(bytes / 1024 / 1024)} MB`;
}

function getHeaderLabel(header, headers) {
    const hasDuplicate = headers.some(candidate => (
        candidate.id !== header.id && candidate.label === header.label
    ));
    return hasDuplicate ? `${header.label} (column ${header.index + 1})` : header.label;
}

/**
 * Generic delimited-text importer with configurable column mappings and submission behavior.
 *
 * fields: [{ name, label, description, required, aliases }]
 * onImport(record, context): imports one record. Individual calls run in configurable batches.
 * onBulkImport(records, context): imports all records in one operation when supplied.
 */
export default function BulkImporter({
    title = 'Import from file',
    intro,
    fields = [],
    onImport,
    onBulkImport,
    onComplete,
    onClose,
    validateRecord,
    transformRecord,
    importLabel = 'Import records',
    recordName = 'record',
    batchSize = 1,
    maxRows = 1000,
    maxFileBytes = 5 * 1024 * 1024,
    accept = DEFAULT_ACCEPT,
}) {
    const [fileData, setFileData] = useState(null);
    const [mappings, setMappings] = useState({});
    const [fileError, setFileError] = useState(null);
    const [status, setStatus] = useState('idle');
    const [progress, setProgress] = useState({ completed: 0, total: 0 });
    const [summary, setSummary] = useState(null);
    const [uploadKey, setUploadKey] = useState(0);

    const mappedRows = useMemo(() => {
        if (!fileData) return [];

        return fileData.rows.map((row, rowIndex) => {
            const rowNumber = rowIndex + 2;
            const mapped = fields.reduce((record, field) => {
                const headerId = mappings[field.name];
                const header = fileData.headers.find(candidate => candidate.id === headerId);
                return {
                    ...record,
                    [field.name]: header ? row[header.index].trim() : '',
                };
            }, {});
            const errors = fields
                .filter(field => field.required && !mapped[field.name])
                .map(field => `${field.label} is required.`);

            try {
                errors.push(...normalizeErrors(validateRecord?.(mapped, {
                    fileName: fileData.name,
                    rowNumber,
                    rawRow: row,
                })));
                const record = errors.length === 0 && transformRecord
                    ? transformRecord(mapped, {
                        fileName: fileData.name,
                        rowNumber,
                        rawRow: row,
                    })
                    : mapped;
                return { rowNumber, mapped, record, errors };
            } catch (error) {
                return {
                    rowNumber,
                    mapped,
                    record: mapped,
                    errors: [...errors, error.message || 'This row could not be prepared.'],
                };
            }
        });
    }, [fields, fileData, mappings, transformRecord, validateRecord]);

    const requiredMappingsMissing = fields.some(field => (
        field.required && !mappings[field.name]
    ));
    const validationErrors = mappedRows.flatMap(row => (
        row.errors.map(error => ({ rowNumber: row.rowNumber, error }))
    ));
    const isImporting = status === 'importing';

    const loadText = (text, fileName, delimiter) => {
        const parsed = parseDelimitedText(text, { delimiter, fileName });
        if (parsed.rows.length > maxRows) {
            throw new Error(`This file has ${parsed.rows.length} rows. The limit is ${maxRows}.`);
        }

        setFileData({ ...parsed, text, name: fileName });
        setMappings(autoMapFields(fields, parsed.headers));
        setSummary(null);
        setStatus('idle');
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setFileError(null);
        try {
            if (file.size > maxFileBytes) {
                const sizeLimit = formatFileSize(maxFileBytes);
                throw new Error(`The selected file is larger than ${sizeLimit}.`);
            }
            const text = await readFile(file);
            loadText(text, file.name, detectDelimiter(text, file.name));
        } catch (error) {
            setFileData(null);
            setMappings({});
            setFileError(error.message || 'The selected file could not be parsed.');
        }
    };

    const handleDelimiterChange = (event) => {
        setFileError(null);
        try {
            loadText(fileData.text, fileData.name, event.target.value);
        } catch (error) {
            setFileError(error.message || 'The file could not be parsed with that delimiter.');
        }
    };

    const handleMappingChange = (fieldName, headerId) => {
        setMappings(current => ({ ...current, [fieldName]: headerId }));
        setSummary(null);
        setStatus('idle');
    };

    const resetFile = () => {
        setFileData(null);
        setMappings({});
        setFileError(null);
        setSummary(null);
        setStatus('idle');
        setUploadKey(key => key + 1);
    };

    const handleImport = async (rowsToImport = mappedRows) => {
        if (requiredMappingsMissing || validationErrors.length > 0 || rowsToImport.length === 0) {
            return;
        }
        if (!onBulkImport && !onImport) {
            setFileError('No import handler is configured.');
            return;
        }

        setStatus('importing');
        setSummary(null);
        setProgress({ completed: 0, total: rowsToImport.length });
        const results = [];

        if (onBulkImport) {
            try {
                await onBulkImport(rowsToImport.map(row => row.record), {
                    fileName: fileData.name,
                    rowNumbers: rowsToImport.map(row => row.rowNumber),
                });
                rowsToImport.forEach(row => results.push({ ...row, success: true }));
            } catch (error) {
                rowsToImport.forEach(row => results.push({
                    ...row,
                    success: false,
                    error: error.message || 'Bulk import failed.',
                }));
            }
            setProgress({ completed: rowsToImport.length, total: rowsToImport.length });
        } else {
            const safeBatchSize = Math.max(1, Number.parseInt(batchSize, 10) || 1);
            for (let index = 0; index < rowsToImport.length; index += safeBatchSize) {
                const batch = rowsToImport.slice(index, index + safeBatchSize);
                const batchResults = await Promise.all(batch.map(async row => {
                    try {
                        await onImport(row.record, {
                            fileName: fileData.name,
                            rowNumber: row.rowNumber,
                        });
                        return { ...row, success: true };
                    } catch (error) {
                        return {
                            ...row,
                            success: false,
                            error: error.message || 'Import failed.',
                        };
                    }
                }));
                results.push(...batchResults);
                setProgress({ completed: results.length, total: rowsToImport.length });
            }
        }

        const nextSummary = {
            successful: results.filter(result => result.success).length,
            failed: results.filter(result => !result.success).length,
            results,
        };
        setSummary(nextSummary);
        setStatus('complete');
        onComplete?.(nextSummary);
    };

    const selectedHeaderIds = new Set(Object.values(mappings).filter(Boolean));
    const previewFields = fields.filter(field => mappings[field.name]);
    const failedResults = summary?.results.filter(result => !result.success) || [];

    return (
        <Modal
            isOpen={true}
            onClose={isImporting ? undefined : onClose}
            showCloseButton={!isImporting}
            closeOnEscape={!isImporting}
            closeOnOverlayClick={!isImporting}
            title={title}
            size="large"
            className="bulk-importer-modal"
        >
            <div className="bulk-importer">
                {intro && <div className="bulk-importer__intro">{intro}</div>}

                <div className="bulk-importer__upload">
                    <label htmlFor="bulk-import-file">Choose a delimited text file</label>
                    <input
                        key={uploadKey}
                        id="bulk-import-file"
                        type="file"
                        accept={accept}
                        onChange={handleFileChange}
                        disabled={isImporting}
                    />
                    <small>
                        Supports CSV, TSV, semicolon-separated, pipe-separated, and text files.
                    </small>
                </div>

                {fileError && <div className="error bulk-importer__alert">{fileError}</div>}

                {fileData && (
                    <>
                        <div className="bulk-importer__file-summary">
                            <div>
                                <strong>{fileData.name}</strong>
                                <span>
                                    {fileData.rows.length} {recordName}
                                    {fileData.rows.length === 1 ? '' : 's'} found
                                </span>
                            </div>
                            <label>
                                File delimiter
                                <select
                                    value={fileData.delimiter}
                                    onChange={handleDelimiterChange}
                                    disabled={isImporting}
                                >
                                    {DELIMITER_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <button
                                type="button"
                                className="button secondary small"
                                onClick={resetFile}
                                disabled={isImporting}
                            >
                                Choose another file
                            </button>
                        </div>

                        <section className="bulk-importer__section">
                            <div className="bulk-importer__section-heading">
                                <h3>Map file columns</h3>
                                <p>Required fields must be mapped before importing.</p>
                            </div>
                            <div className="bulk-importer__mappings">
                                {fields.map(field => (
                                    <label className="bulk-importer__mapping" key={field.name}>
                                        <span>
                                            <strong>{field.label}</strong>
                                            {field.required
                                                ? <em>Required</em>
                                                : <em>Optional</em>}
                                        </span>
                                        {field.description && <small>{field.description}</small>}
                                        <select
                                            aria-label={`Map ${field.label}`}
                                            value={mappings[field.name] || ''}
                                            onChange={event => (
                                                handleMappingChange(field.name, event.target.value)
                                            )}
                                            disabled={isImporting}
                                        >
                                            <option value="">Do not import</option>
                                            {fileData.headers.map(header => (
                                                <option
                                                    key={header.id}
                                                    value={header.id}
                                                    disabled={selectedHeaderIds.has(header.id)
                                                        && mappings[field.name] !== header.id}
                                                >
                                                    {getHeaderLabel(header, fileData.headers)}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                ))}
                            </div>
                        </section>

                        {previewFields.length > 0 && (
                            <section className="bulk-importer__section">
                                <div className="bulk-importer__section-heading">
                                    <h3>Preview</h3>
                                    <p>
                                        Showing the first {Math.min(5, mappedRows.length)}
                                        {' '}mapped rows.
                                    </p>
                                </div>
                                <div className="bulk-importer__preview">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>File row</th>
                                                {previewFields.map(field => (
                                                    <th key={field.name}>{field.label}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {mappedRows.slice(0, 5).map(row => (
                                                <tr key={row.rowNumber}>
                                                    <td>{row.rowNumber}</td>
                                                    {previewFields.map(field => (
                                                        <td key={field.name}>
                                                            {row.mapped[field.name] || (
                                                                <span className="muted">Empty</span>
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}

                        {(requiredMappingsMissing || validationErrors.length > 0) && (
                            <div className="error bulk-importer__alert">
                                {requiredMappingsMissing && (
                                    <p>Map every required field to a file column.</p>
                                )}
                                {validationErrors.length > 0 && (
                                    <>
                                        <p>Fix these rows in the source file before importing:</p>
                                        <ul>
                                            {validationErrors.slice(0, 10).map((item, index) => (
                                                <li key={`${item.rowNumber}-${index}`}>
                                                    Row {item.rowNumber}: {item.error}
                                                </li>
                                            ))}
                                        </ul>
                                        {validationErrors.length > 10 && (
                                            <p>And {validationErrors.length - 10} more errors.</p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {isImporting && (
                            <div className="bulk-importer__progress" aria-live="polite">
                                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                                Imported {progress.completed} of {progress.total} {recordName}s...
                            </div>
                        )}

                        {summary && (
                            <div
                                className={
                                    `bulk-importer__result ${summary.failed ? 'error' : 'success'}`
                                }
                                aria-live="polite"
                            >
                                <strong>
                                    {summary.successful} {recordName}
                                    {summary.successful === 1 ? '' : 's'} imported.
                                </strong>
                                {summary.failed > 0 && (
                                    <>
                                        <p>{summary.failed} failed:</p>
                                        <ul>
                                            {failedResults
                                                .slice(0, 20)
                                                .map(result => (
                                                    <li key={result.rowNumber}>
                                                        Row {result.rowNumber}: {result.error}
                                                    </li>
                                                ))}
                                        </ul>
                                        {failedResults.length > 20 && (
                                            <p>And {failedResults.length - 20} more failures.</p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}

                <div className="bulk-importer__actions">
                    <button
                        type="button"
                        className="button secondary"
                        onClick={onClose}
                        disabled={isImporting}
                    >
                        {summary && summary.failed === 0 ? 'Close' : 'Cancel'}
                    </button>
                    {fileData && !summary && (
                        <button
                            type="button"
                            className="button primary"
                            onClick={() => handleImport()}
                            disabled={isImporting || requiredMappingsMissing
                                || validationErrors.length > 0 || mappedRows.length === 0}
                        >
                            <i className="fas fa-file-import" aria-hidden="true"></i>
                            {isImporting ? 'Importing...' : `${importLabel} (${mappedRows.length})`}
                        </button>
                    )}
                    {summary?.failed > 0 && (
                        <button
                            type="button"
                            className="button primary"
                            onClick={() => handleImport(failedResults)}
                        >
                            Retry failed {recordName}s ({summary.failed})
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
}
